<template>
    <ContentModal v-model="show" :title="title || 'Summary'" size="xl">
        <div class="summary-modal" v-if="summaryList && summaryList.length">
            <SummarySectionMenu :sections="sectionMenuItems" />
            <div v-for="group in groupedItems" :id="group.id" :key="group.id" class="sm-category">
                <h6 class="sm-category-title">
                    <i class="material-icons">{{ categoryIcon(group.category) }}</i>
                    {{ group.category }}
                </h6>
                <div v-for="(item, iIdx) in group.items" :key="iIdx" class="sm-item">
                    <i class="material-icons sm-item-icon">{{ item.icon || 'info' }}</i>
                    <div class="sm-item-text">
                        <template v-if="item.rule_title">
                            <strong>{{ item.rule_title }}</strong>
                            <p><b>Configured:</b> {{ item.rule_configuration }}</p>
                            <p><b>What this means:</b> {{ item.rule_explanation }}</p>
                        </template>
                        <template v-else-if="item.rule_notice">{{ item.rule_notice }}</template>
                        <span v-else v-html="renderMarkdown(item.text)"></span>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="text-center py-4 text-muted">
            <i class="material-icons" style="font-size: 48px; opacity: 0.3;">summarize</i>
            <p class="mt-2">No summary data available.</p>
        </div>
    </ContentModal>
</template>

<script setup>
import { computed } from 'vue'
import ContentModal from './ContentModal.vue'
import SummarySectionMenu from './SummarySectionMenu.vue'

const props = defineProps({
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: 'Summary' },
    summaryList: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue'])

const show = computed({
    get: () => props.modelValue,
    set: (v) => emit('update:modelValue', v),
})

const groupedItems = computed(() => {
    const groups = {}
    for (const item of (props.summaryList || [])) {
        const cat = item.category || 'General'
        if (!groups[cat]) groups[cat] = { category: cat, items: [] }
        groups[cat].items.push(item)
    }
    return Object.values(groups).map((group, index) => ({
        ...group,
        id: `summary-section-${index}-${group.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    }))
})

const sectionMenuItems = computed(() => groupedItems.value.map(({ id, category }) => ({
    id,
    label: category,
})))

const categoryIcon = (cat) => {
    const icons = {
        'General': 'info',
        'Scheduling': 'event_repeat',
        'Visibility': 'visibility',
        'Notifications': 'notifications',
        'Structure': 'account_tree',
        'Instances': 'calendar_today',
        'Teams': 'groups',
        'Registration': 'how_to_reg',
        'Account': 'manage_accounts',
        'Skills': 'workspace_premium',
        'Activity': 'timeline',
        'Scheduling Rules': 'rule',
    }
    return icons[cat] || 'label'
}

const renderMarkdown = (text) => {
    if (!text) return ''
    // Convert **bold** to <strong>
    return text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
}
</script>

<style scoped>
.summary-modal { max-height: 70vh; overflow-y: auto; }
.sm-category { margin-bottom: 20px; scroll-margin-top: 72px; }
.sm-category-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem;
    font-weight: 700;
    color: #4f46e5;
    margin-bottom: 10px;
    padding-bottom: 6px;
    border-bottom: 2px solid #e8eaf6;
}
.sm-category-title i { font-size: 20px; }
.sm-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 14px;
    background: #f8fafc;
    border-radius: 8px;
    margin-bottom: 6px;
    font-size: 0.84rem;
    color: #344767;
    line-height: 1.5;
    border-left: 3px solid #e2e8f0;
    transition: border-color 0.15s, background 0.15s;
}
.sm-item:hover {
    background: #f1f5f9;
    border-left-color: #4f46e5;
}
.sm-item-icon {
    font-size: 18px;
    color: #7b809a;
    margin-top: 1px;
    flex-shrink: 0;
}
.sm-item-text :deep(strong) {
    color: #1e293b;
    font-weight: 700;
}
.sm-item-text p { margin: 5px 0 0; }
</style>
